import {POST} from '../modules/fetch.ts';
import {registerGlobalInitFunc} from '../modules/observer.ts';

export type PackagePreviewData = {
  owners: Array<{
    name: string,
    packages: Array<{type: string, name: string, included: boolean}>,
    more: number,
    unmatched: string[],
  }>,
  errors: Array<{line: number, reason: string}>,
};

export type PackagePreviewLabels = {included: string, excluded: string, unmatched: string, more: string, error: string};

function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? ''));
}

// renderPackagePreview replaces the container's content. All text goes through textContent, never innerHTML.
export function renderPackagePreview(container: HTMLElement, data: PackagePreviewData, labels: PackagePreviewLabels) {
  container.replaceChildren();
  for (const err of data.errors) {
    const div = document.createElement('div');
    div.className = 'ui negative message package-rules-preview-error';
    div.textContent = fill(labels.error, {line: err.line, reason: err.reason});
    container.append(div);
  }
  for (const owner of data.owners) {
    const section = document.createElement('div');
    section.className = 'package-rules-preview-owner';
    const title = document.createElement('strong');
    title.textContent = owner.name;
    section.append(title);
    const list = document.createElement('ul');
    list.className = 'tw-my-1';
    for (const pkg of owner.packages) {
      const li = document.createElement('li');
      li.className = pkg.included ? 'package-rules-included' : 'package-rules-excluded';
      li.textContent = `${pkg.included ? labels.included : labels.excluded}: ${pkg.type} ${pkg.name}`;
      list.append(li);
    }
    section.append(list);
    if (owner.more > 0) {
      const more = document.createElement('div');
      more.className = 'help';
      more.textContent = fill(labels.more, {count: owner.more});
      section.append(more);
    }
    for (const rule of owner.unmatched) {
      const note = document.createElement('div');
      note.className = 'help package-rules-unmatched';
      note.textContent = `${rule}: ${labels.unmatched}`;
      section.append(note);
    }
    container.append(section);
  }
}

function initPackageRulesPreview(container: HTMLElement) {
  const textarea = document.querySelector<HTMLTextAreaElement>(container.getAttribute('data-textarea')!);
  if (!textarea) return;
  const ownersSel = container.getAttribute('data-owners');
  const ownersInput = ownersSel ? document.querySelector<HTMLInputElement>(ownersSel) : null;
  const url = container.getAttribute('data-url')!;
  const labels: PackagePreviewLabels = {
    included: container.getAttribute('data-label-included')!,
    excluded: container.getAttribute('data-label-excluded')!,
    unmatched: container.getAttribute('data-label-unmatched')!,
    more: container.getAttribute('data-label-more')!,
    error: container.getAttribute('data-label-error')!,
  };

  let timer = 0;
  let seq = 0;
  const refresh = async () => {
    const mine = ++seq;
    const body = new URLSearchParams({packages: textarea.value, owners: ownersInput?.value ?? ''});
    try {
      const resp = await POST(url, {data: body});
      if (mine !== seq) return; // a newer request superseded this one
      if (!resp.ok) {
        container.replaceChildren();
        return;
      }
      renderPackagePreview(container, await resp.json() as PackagePreviewData, labels);
    } catch {
      if (mine === seq) container.replaceChildren();
    }
  };
  const schedule = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(refresh, 300);
  };
  textarea.addEventListener('input', schedule);
  ownersInput?.addEventListener('input', schedule);
  if (textarea.value.trim()) refresh();
}

export function initPackageRulesPreviewComponent() {
  registerGlobalInitFunc('initPackageRulesPreview', initPackageRulesPreview);
}
